import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import Markdown from "@/components/Markdown";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PaperPlaneTilt, Sparkle, Scissors, FloppyDisk, DeviceMobile, Desktop, ChatCircle, Plus, Copy, Eye, EyeSlash, Info, ClockCounterClockwise, PencilSimple, Trash, CircleNotch } from "@phosphor-icons/react";
import { useConfirm as useConfirmDialog } from "@/components/ConfirmProvider";
import { Group as PanelGroup, Panel, Separator as PanelResizeHandle, useDefaultLayout } from "react-resizable-panels";
import GrowthScore from "@/components/GrowthScore";

const TONE_HINT = {
  personal: "Intimate, first-person, warm. Vulnerability, sensory detail, more 'I'.",
  balanced: "Your default voice. Insight + story + practical takeaway. Confident + human.",
  corporate: "Strategic, exec-ready. Frameworks and metrics. Fewer emotional beats.",
  contrarian: "Bold, myth-busting. Lead with a spicy take. Invite pushback.",
};

const POST_TYPE_META = {
  growth: { label: "Growth", hint: "Reach new audiences. Trend takes, industry observations, challenges to common beliefs." },
  authority: { label: "Authority", hint: "Show expertise. Frameworks, case studies, step-by-step methods." },
  conversion: { label: "Conversion", hint: "Turn followers into leads. One clear CTA at the end." },
  personal: { label: "Personal", hint: "Human connection. Behind-the-scenes, founder lessons, milestones." },
};

const CHAR_GUIDANCE = (n) => {
  if (n === 0) return null;
  if (n < 400) return { tone: "warn", text: `Only ${n} chars. Add specifics, a mini-story beat, or a comment trigger. Long-form (900-1600) usually outperforms.` };
  if (n < 900) return { tone: "warn", text: `${n} chars. Solid short post. Consider adding one more concrete detail or a mini list to boost dwell time.` };
  if (n <= 1600) return { tone: "good", text: `${n} chars. Sweet spot on LinkedIn (900-1600). Great dwell + save potential.` };
  if (n <= 2400) return { tone: "good", text: `${n} chars. Long-form. Make sure line breaks are frequent so it doesn't feel dense.` };
  if (n <= 3000) return { tone: "warn", text: `${n} chars. Getting close to the 3000 LinkedIn ceiling. Tighten filler and cut throat-clearing lines.` };
  return { tone: "bad", text: `${n} chars. Over the 3000 LinkedIn limit. Trim aggressively.` };
};

const HOOKS = [
  "I fired my ghostwriter last Tuesday.",
  "The riskiest thing you can do in 2026 isn't public. It's silent.",
  "Most personal brands sound like the same person. Here's why.",
  "Everyone told me to niche down. I did the opposite.",
  "I stopped posting for a month. My inbound tripled.",
];

const SESSION_KEY = "bl_draft_session";
const DRAFT_STATE_KEY = "bl_draft_state"; // per-session cached UI state (postText, input, tone, useSwipes)

function loadDraftState(sessionId) {
  try {
    const raw = localStorage.getItem(DRAFT_STATE_KEY);
    if (!raw) return null;
    const all = JSON.parse(raw);
    return all?.[sessionId] || null;
  } catch { return null; }
}
function saveDraftState(sessionId, patch) {
  try {
    const raw = localStorage.getItem(DRAFT_STATE_KEY);
    const all = raw ? JSON.parse(raw) : {};
    all[sessionId] = { ...(all[sessionId] || {}), ...patch };
    localStorage.setItem(DRAFT_STATE_KEY, JSON.stringify(all));
  } catch {}
}
function clearDraftState(sessionId) {
  try {
    const raw = localStorage.getItem(DRAFT_STATE_KEY);
    if (!raw) return;
    const all = JSON.parse(raw);
    delete all[sessionId];
    localStorage.setItem(DRAFT_STATE_KEY, JSON.stringify(all));
  } catch {}
}

// Strip common AI preambles like "Here's your LinkedIn post:" or wrapping quotes.
// Also strips stray "===" separator artifacts that occasionally leak into the post copy.
function cleanAiPost(text) {
  if (!text) return "";
  let t = text.trim();
  const preambleRe = /^(here'?s|here is|sure!|of course|absolutely|here you go|below is|okay,? here'?s|got it,? here'?s)[^\n]{0,80}[:.\-]\s*\n+/i;
  t = t.replace(preambleRe, "");
  t = t.replace(/^```[a-z]*\n?/i, "").replace(/\n?```$/, "");
  t = t.replace(/^["'"']\s*/, "").replace(/\s*["'"']$/, "");
  // Kill any === separator sequences (from the ===EXPLANATION=== convention) that landed in the post body.
  t = t.replace(/={3,}[A-Z ]*={3,}/g, "");   // remove ===POST===, ===EXPLANATION=== style markers
  t = t.replace(/^={3,}[^\n]*$/gm, "");      // remove any standalone === line
  t = t.replace(/\n{3,}/g, "\n\n").trim();   // collapse the extra whitespace left behind
  return t.trim();
}

function AutoTextarea({ value, onChange, placeholder, onEnter, dataTestid, disabled }) {
  const ref = useRef(null);
  useEffect(() => {
    if (!ref.current) return;
    ref.current.style.height = "auto";
    const next = Math.min(ref.current.scrollHeight, 220);
    ref.current.style.height = next + "px";
  }, [value]);
  return (
    <textarea
      ref={ref}
      rows={1}
      value={value}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault();
          onEnter?.();
        }
      }}
      placeholder={placeholder}
      data-testid={dataTestid}
      className="textarea resize-none scroll-thin text-base"
      style={{ minHeight: 48, maxHeight: 220, overflow: "auto", fontSize: 16, lineHeight: 1.55 }}
    />
  );
}

// HTML escape
function escHtml(s) {
  return (s || "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
}

/**
 * Renders the draft text with grammar-fix `before` substrings highlighted.
 * We do a simple sequential replacement in original-index order (longest-first to avoid
 * nesting collisions). Each highlight is clickable and applies the fix.
 */
function InlineHighlightView({ text, fixes, onApplyFix }) {
  const html = useMemo(() => {
    if (!fixes || !fixes.length) return escHtml(text);
    // Find non-overlapping matches for each fix, prefer longest-first
    const sorted = [...fixes].sort((a, b) => b.before.length - a.before.length);
    const marks = []; // {start, end, fixIdx}
    for (const f of sorted) {
      const idx = fixes.indexOf(f);
      let searchFrom = 0;
      while (true) {
        const at = text.indexOf(f.before, searchFrom);
        if (at === -1) break;
        const end = at + f.before.length;
        // Check overlap with existing marks
        const overlap = marks.some((m) => at < m.end && end > m.start);
        if (!overlap) {
          marks.push({ start: at, end, fixIdx: idx });
          break; // one highlight per fix
        }
        searchFrom = at + 1;
      }
    }
    marks.sort((a, b) => a.start - b.start);
    let out = "";
    let cursor = 0;
    for (const m of marks) {
      out += escHtml(text.slice(cursor, m.start));
      const f = fixes[m.fixIdx];
      const reasonLine = f.reason ? escHtml(f.reason) + ". " : "";
      out += `<mark class="bl-inline-fix" data-fix-idx="${m.fixIdx}" data-after="${escHtml(f.after)}" title="${reasonLine}Click to apply">${escHtml(text.slice(m.start, m.end))}</mark>`;
      cursor = m.end;
    }
    out += escHtml(text.slice(cursor));
    return out;
  }, [text, fixes]);

  const containerRef = useRef(null);
  useEffect(() => {
    if (!containerRef.current) return;
    const marks = containerRef.current.querySelectorAll("mark.bl-inline-fix");
    const handler = (e) => {
      const idx = parseInt(e.currentTarget.getAttribute("data-fix-idx"), 10);
      if (!isNaN(idx) && fixes[idx]) onApplyFix(fixes[idx]);
    };
    marks.forEach((m) => m.addEventListener("click", handler));
    return () => marks.forEach((m) => m.removeEventListener("click", handler));
  }, [html, fixes, onApplyFix]);

  return (
    <div
      ref={containerRef}
      data-testid="draft-inline-view"
      className="rounded-2xl p-3 whitespace-pre-wrap"
      style={{ background: "var(--bg-soft)", border: "1px solid var(--line-2)", minHeight: 360, lineHeight: 1.65, fontSize: 17, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Code", "Segoe UI Mono", "Roboto Mono", monospace' }}
      dangerouslySetInnerHTML={{ __html: html || "<span style='color:var(--ink-3)'>Your draft appears here, edit freely.</span>" }}
    />
  );
}

export default function DraftingStudio() {
  const { user, refresh } = useAuth();
  const [sessionId, setSessionId] = useState(() => {
    const stored = localStorage.getItem(SESSION_KEY);
    if (stored) return stored;
    const fresh = `sess-${crypto.randomUUID?.() || Date.now()}`;
    localStorage.setItem(SESSION_KEY, fresh);
    return fresh;
  });
  const cached = loadDraftState(sessionId) || {};
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState(cached.input || "");
  const [busy, setBusy] = useState(false);
  const [tone, setTone] = useState(cached.tone || "balanced");
  const [useSwipes, setUseSwipes] = useState(!!cached.useSwipes);
  const [postType, setPostType] = useState(cached.postType || "growth");
  const [postText, setPostText] = useState(cached.postText || "");
  const [lastAiDraft, setLastAiDraft] = useState(cached.lastAiDraft || ""); // last AI-generated version (for diff)
  const [device, setDevice] = useState("mobile");
  const [showPreview, setShowPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [polishing, setPolishing] = useState(false);
  const [polishSuggestions, setPolishSuggestions] = useState([]);
  const [showPolish, setShowPolish] = useState(false);
  const [sessions, setSessions] = useState([]);
  const [showSessions, setShowSessions] = useState(false);
  const [grammarFixes, setGrammarFixes] = useState([]);
  const [showInline, setShowInline] = useState(false); // toggle inline highlighted view
  const [mode, setMode] = useState(cached.mode || "auto"); // "auto" | "chat" | "post"
  // AI-classified post type (Attract/Nurture/Position/Convert) for the current draft.
  // Auto-refreshed after every generation and any time `postText` settles for 1s+.
  const [suggestedTag, setSuggestedTag] = useState("");
  const [classifying, setClassifying] = useState(false);
  const classifyTimer = useRef(null);
  const scrollRef = useRef(null);

  // Debounced classifier — runs whenever the draft text changes.
  useEffect(() => {
    if (classifyTimer.current) clearTimeout(classifyTimer.current);
    const text = (postText || "").trim();
    if (text.length < 50) { setSuggestedTag(""); return; }
    classifyTimer.current = setTimeout(async () => {
      try {
        setClassifying(true);
        const r = await api.post("/posts/classify-type", { text });
        if (r.data?.tag) setSuggestedTag(r.data.tag);
      } catch {}
      finally { setClassifying(false); }
    }, 1100);
    return () => { if (classifyTimer.current) clearTimeout(classifyTimer.current); };
  }, [postText]);

  // Load saved panel layout via v4 hook (persists to localStorage automatically)
  const { defaultLayout, onLayoutChanged } = useDefaultLayout({
    id: "bl-draft-panels-v1",
    storage: typeof window !== "undefined" ? window.localStorage : undefined,
  });

  const loadSessions = async () => {
    try {
      const r = await api.get("/draft/sessions");
      setSessions(r.data || []);
    } catch {}
  };
  useEffect(() => { loadSessions(); }, []);

  // Persist per-session UI state so unsaved edits and inputs survive reloads
  useEffect(() => { saveDraftState(sessionId, { postText }); }, [postText, sessionId]);
  useEffect(() => { saveDraftState(sessionId, { input }); }, [input, sessionId]);
  useEffect(() => { saveDraftState(sessionId, { tone }); }, [tone, sessionId]);
  useEffect(() => { saveDraftState(sessionId, { useSwipes }); }, [useSwipes, sessionId]);
  useEffect(() => { saveDraftState(sessionId, { postType }); }, [postType, sessionId]);
  useEffect(() => { saveDraftState(sessionId, { lastAiDraft }); }, [lastAiDraft, sessionId]);
  useEffect(() => { saveDraftState(sessionId, { mode }); }, [mode, sessionId]);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/draft/chat/${sessionId}`);
        setMessages(r.data.map(m => ({ role: m.role, content: m.content })));
        const localCached = loadDraftState(sessionId) || {};
        const lastAssistant = [...r.data].reverse().find(m => m.role === "assistant");
        // Only hydrate the editable draft from a chat message that actually contains post copy
        // (POST MODE — marker present). Conversational chat replies must not overwrite the draft.
        if (lastAssistant && (lastAssistant.content || "").includes("===EXPLANATION===")) {
          const [p] = (lastAssistant.content || "").split("===EXPLANATION===");
          const cleaned = cleanAiPost(p);
          if (cleaned && cleaned.length > 20) {
            if (!localCached.lastAiDraft) setLastAiDraft(cleaned);
            if (!localCached.postText) setPostText(cleaned);
          }
        }
      } catch {}
    })();
  }, [sessionId]);

  const scrollToBottom = () => {
    requestAnimationFrame(() => {
      if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    });
  };

  const send = async (text = input, opts = {}) => {
    const finalText = (text || "").trim();
    if (!finalText || busy) return;
    // opts: { prevDraft, currentDraft } for edit-diff mode
    setMessages(m => [...m, { role: "user", content: finalText }]);
    setInput("");
    setBusy(true);
    scrollToBottom();
    try {
      const payload = {
        session_id: sessionId,
        message: finalText,
        tone,
        post_type: postType,
        use_swipes: useSwipes,
        mode,  // "auto" | "chat" | "post" — user-forced reply mode
      };
      // Always send the current draft as context so the AI knows what "the post" refers to
      // (skipped in edit-diff mode below — that path sets prev_draft + current_draft instead)
      if (postText && postText.trim()) {
        payload.context_draft = postText;
      }
      if (opts.prevDraft && opts.currentDraft) {
        payload.prev_draft = opts.prevDraft;
        payload.current_draft = opts.currentDraft;
      }
      const r = await api.post("/draft/chat", payload);
      const reply = r.data.reply || "";
      setMessages(m => [...m, { role: "assistant", content: reply }]);
      // Only overwrite the editable draft when the AI actually returned a new post
      // (POST MODE — marker present). Chat-mode replies (no marker) leave the draft alone.
      if (reply.includes("===EXPLANATION===")) {
        const [post] = reply.split("===EXPLANATION===");
        const cleaned = cleanAiPost(post);
        if (cleaned && cleaned.length > 20) {
          setPostText(cleaned);
          setLastAiDraft(cleaned);
        }
      }
      await refresh();
      loadSessions();
      scrollToBottom();
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(e.response.data.detail || "Trial limit reached. Upgrade to Pro.");
        await refresh();
      } else {
        toast.error(formatApiError(e));
      }
    }
    finally { setBusy(false); }
  };

  const sendEditsToChat = () => {
    if (!postText.trim()) { toast.error("No draft to send yet."); return; }
    const hasPrev = lastAiDraft && lastAiDraft.trim() && lastAiDraft.trim() !== postText.trim();
    if (hasPrev) {
      // Edit-diff mode: ask AI to explain what changed
      send(
        "I edited your last draft. Explain what I changed and why it works (or doesn't), then return a refined version that keeps my intent.",
        { prevDraft: lastAiDraft, currentDraft: postText }
      );
    } else {
      // No prior AI draft OR unchanged — treat as normal refinement request
      send(
        `Please refine this draft. Keep my changes where they work, sharpen the hook and flow, and make sure it still sounds like me.\n\n---\nCURRENT DRAFT:\n${postText}`
      );
    }
  };

  const saveToPlanner = async () => {
    if (!postText.trim()) { toast.error("No draft to save yet."); return; }
    setSaving(true);
    try {
      await api.post("/posts", {
        topic: postText.slice(0, 60),
        content: postText,
        status: "Drafting",
        pillar: "",
        // Carry the AI's classified type into the Planner so the user doesn't have to re-tag it.
        post_type_tag: suggestedTag || "",
      });
      toast.success(
        suggestedTag
          ? `Saved to Planner as 'Drafting' · tagged ${suggestedTag}.`
          : "Saved to Planner as 'Drafting'."
      );
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const copyToClipboard = async () => {
    if (!postText.trim()) { toast.error("Nothing to copy yet."); return; }
    try {
      await navigator.clipboard.writeText(postText);
      toast.success("Copied. Paste it into LinkedIn.");
    } catch {
      toast.error("Clipboard blocked. Select and copy manually.");
    }
  };

  const runPolish = async () => {
    if (!postText.trim()) return;
    setPolishing(true);
    setPolishSuggestions([]);
    try {
      const r = await api.post("/draft/polish", { text: postText });
      setPolishSuggestions(r.data.suggestions || []);
      setShowPolish(true);
      if (!(r.data.suggestions || []).length) toast.success("Draft looks clean. Nothing to fix.");
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setPolishing(false); }
  };

  const applyPolishSuggestion = (s) => {
    setPostText(prev => prev.replace(s.before, s.after));
    setPolishSuggestions(prev => prev.filter(x => x !== s));
    toast.success("Applied.");
  };

  const applyAllPolish = () => {
    let next = postText;
    polishSuggestions.forEach(s => { next = next.replace(s.before, s.after); });
    setPostText(next);
    setPolishSuggestions([]);
    setShowPolish(false);
    toast.success("All fixes applied.");
  };

  // Apply a single grammar fix (from GrowthScore panel or inline highlight click)
  // Supports either a legacy grammar fix ({before, after}) or a proposed improvement
  // ({__fullText, __append}) which carries an already-patched full body.
  const applyGrammarFix = (fix) => {
    if (fix && typeof fix.__fullText === "string") {
      setPostText(fix.__fullText);
    } else {
      setPostText((prev) => {
        if (!fix?.before || !prev.includes(fix.before)) return prev;
        return prev.replace(fix.before, fix.after);
      });
    }
    setGrammarFixes((prev) => prev.filter((f) => f !== fix));
    if (fix?.__append) toast.success("Added to your draft.");
    else if (fix?.__fullText && !fix?.before) toast.success("Improvements applied.");
    else toast.success("Fix applied.");
  };

  const newChat = () => {
    clearDraftState(sessionId);
    const fresh = `sess-${crypto.randomUUID?.() || Date.now()}`;
    localStorage.setItem(SESSION_KEY, fresh);
    setSessionId(fresh);
    setMessages([]);
    setPostText("");
    setLastAiDraft("");
    setInput("");
    setGrammarFixes([]);
    setMode("auto");
    toast.success("New draft session started.");
    loadSessions();
  };

  const switchSession = (sid) => {
    if (sid === sessionId) { setShowSessions(false); return; }
    localStorage.setItem(SESSION_KEY, sid);
    setSessionId(sid);
    const cachedS = loadDraftState(sid) || {};
    setInput(cachedS.input || "");
    setTone(cachedS.tone || "balanced");
    setUseSwipes(!!cachedS.useSwipes);
    setPostText(cachedS.postText || "");
    setLastAiDraft(cachedS.lastAiDraft || "");
    setMode(cachedS.mode || "auto");
    setMessages([]);
    setGrammarFixes([]);
    setShowSessions(false);
  };

  const renameSession = async (sid, currentTitle) => {
    const next = window.prompt("Rename this chat:", currentTitle || "");
    if (!next || !next.trim()) return;
    try {
      await api.put(`/draft/sessions/${sid}/rename`, { title: next.trim() });
      toast.success("Renamed.");
      loadSessions();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const confirm = useConfirmDialog();
  const deleteSession = async (sid, title) => {
    const ok = await confirm({
      title: "Delete this chat?",
      description: `The conversation "${title}" will be removed. This can't be restored right now.`,
      confirmText: "Delete chat",
    });
    if (!ok) return;
    try {
      await api.delete(`/draft/sessions/${sid}`);
      toast.success("Chat deleted.");
      if (sid === sessionId) newChat();
      else loadSessions();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const authorName = user?.name || user?.email?.split("@")[0] || "You";
  const initial = (authorName || "?").slice(0, 1).toUpperCase();
  const previewText = useMemo(() => postText || "Your LinkedIn draft will appear here. Ask her for a hook, a story, a contrarian take. She'll write it in your voice.", [postText]);
  const hasEdits = !!(lastAiDraft && lastAiDraft.trim() && lastAiDraft.trim() !== postText.trim());

  // -- Rendered panel content extracted for clarity --
  const ChatPane = (
    <section className="card p-0 flex flex-col min-h-0 overflow-hidden h-full">
      <div className="p-4 flex items-center gap-3 shrink-0">
        <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{background:'var(--accent)', color:'#fff'}}><Sparkle size={16} weight="fill"/></div>
        <div className="min-w-0">
          <div className="font-medium text-sm">Your brand manager</div>
          <div className="text-xs truncate" style={{color:'var(--ink-3)'}}>Trained on your identity, KB, storybank</div>
        </div>
      </div>
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto scroll-thin p-4 space-y-4" data-testid="draft-messages">
        {messages.length === 0 && (
          <div>
            <p className="font-serif text-2xl leading-snug mb-4 font-semibold">Hi {authorName.split(" ")[0]}. What are we writing today?</p>
            <div className="flex flex-wrap gap-2 mb-3">
              {HOOKS.map((h,i)=>(
                <button key={i} data-testid={`draft-hook-${i}`} onClick={()=>send(`Write a LinkedIn post using this hook, in my voice: "${h}"`)} className="chip hover:border-black">{h}</button>
              ))}
            </div>
            <p className="text-sm" style={{color:'var(--ink-3)'}}>Or type your own prompt below.</p>
          </div>
        )}
        {messages.map((m,i)=>{
          if (m.role === 'user') {
            return (
              <div key={i} className="fade-up text-right">
                <div className="inline-block max-w-[92%] px-4 py-3 text-left" style={{ background:'var(--ink)', color:'#fff', borderRadius:'22px 22px 6px 22px', whiteSpace:'pre-wrap', fontSize: 16, lineHeight: 1.55 }}>
                  {m.content}
                </div>
              </div>
            );
          }
          const [postPart, explanationPart] = (m.content || "").split("===EXPLANATION===");
          const post = (postPart || "").trim();
          const explanation = (explanationPart || "").trim();
          return (
            <div key={i} className="fade-up space-y-2">
              <div className="inline-block max-w-[92%] px-4 py-3 text-left" style={{ background:'var(--paper)', color:'var(--ink)', borderRadius:'22px 22px 22px 6px', fontSize: 16, lineHeight: 1.55 }}>
                <Markdown>{post}</Markdown>
              </div>
              {explanation && (
                <div className="max-w-[92%] px-4 py-3 text-xs text-left rounded-2xl" data-testid={`draft-explanation-${i}`} style={{ background:'var(--accent-tint)', color:'var(--ink-1)', border:'1px solid var(--accent)' }}>
                  <div className="eyebrow mb-2 flex items-center gap-1.5" style={{color:'var(--accent-2)'}}>
                    <Sparkle size={11} weight="fill"/> Why this works
                  </div>
                  <Markdown>{explanation}</Markdown>
                </div>
              )}
            </div>
          );
        })}
        {busy && <div className="text-sm" style={{color:'var(--ink-3)'}}>She's thinking…</div>}
      </div>
      <div className="p-3 flex gap-2 items-end shrink-0">
        <AutoTextarea
          value={input}
          onChange={setInput}
          onEnter={()=>send()}
          placeholder="Ask for a post, a rewrite, or share a brief…"
          dataTestid="draft-input"
          disabled={busy}
        />
        <button data-testid="draft-send" onClick={()=>send()} disabled={busy||!input.trim()} className="btn btn-accent shrink-0" style={{ height: 44 }}>
          <PaperPlaneTilt size={16}/>
        </button>
      </div>
    </section>
  );

  const DraftPane = (
    <div className="h-full flex flex-col min-h-0 overflow-y-auto scroll-thin pr-1">
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <div className="eyebrow">Editable draft</div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-mono" style={{color:'var(--ink-3)'}} data-testid="draft-charcount">
            {postText.length} of 900-1600 recommended
          </span>
          <button data-testid="draft-polish" onClick={runPolish} disabled={polishing||!postText.trim()} className="btn btn-ghost text-xs py-1.5 px-3" title="Grammar, punctuation, clarity fixes">{polishing ? <><CircleNotch size={13} className="animate-spin"/> Polishing</> : <><Sparkle size={13}/> Polish</>}</button>
          <button data-testid="draft-copy" onClick={copyToClipboard} disabled={!postText.trim()} className="btn btn-ghost text-xs py-1.5 px-3" title="Copy draft to clipboard"><Copy size={14}/> Copy</button>
          <button
            data-testid="draft-send-edits"
            onClick={sendEditsToChat}
            disabled={busy||!postText.trim()}
            className="btn btn-ghost text-xs py-1.5 px-3"
            title={hasEdits ? "You edited the AI's draft. Send changes so she can explain and refine." : "Send draft to chat for refinement."}
          >
            <ChatCircle size={14}/> {hasEdits ? "Explain my edits" : "Send edits"}
          </button>
        </div>
      </div>

      {(() => {
        const g = CHAR_GUIDANCE(postText.length);
        if (!g) return null;
        const styleMap = {
          good: { background: 'var(--accent-tint)', color: 'var(--accent-2)', border: '1px solid var(--accent)' },
          warn: { background: 'var(--bg-soft)', color: 'var(--ink-2)', border: '1px solid var(--line-2)' },
          bad: { background: '#fde5df', color: '#8f2b1a', border: '1px solid #b83a2b' },
        };
        return (
          <div data-testid="char-guidance" className="text-xs px-3 py-2 rounded-2xl mb-3 flex items-start gap-2" style={styleMap[g.tone]}>
            <Info size={12} className="mt-0.5 shrink-0"/><span>{g.text}</span>
          </div>
        );
      })()}

      {showInline ? (
        <InlineHighlightView text={postText} fixes={grammarFixes} onApplyFix={applyGrammarFix} />
      ) : (
        <textarea
          data-testid="draft-editor"
          className="textarea min-h-[360px] h-full"
          value={postText}
          onChange={e => setPostText(e.target.value)}
          onPaste={(e) => {
            const pasted = e.clipboardData?.getData("text");
            if (pasted == null) return;
            const cleaned = pasted.replace(/\r\n?/g, "\n").replace(/[ \t]+\n/g, "\n").replace(/\n{3,}/g, "\n\n");
            if (cleaned === pasted) return;
            e.preventDefault();
            const el = e.currentTarget;
            const start = el.selectionStart ?? el.value.length;
            const end = el.selectionEnd ?? el.value.length;
            setPostText(el.value.slice(0, start) + cleaned + el.value.slice(end));
            requestAnimationFrame(() => { try { const pos = start + cleaned.length; el.setSelectionRange(pos, pos); } catch {} });
          }}
          placeholder="Your draft appears here, edit freely. Add [notes in brackets] and click 'Explain my edits' to have her refine."
          style={{ fontSize: 17, lineHeight: 1.65, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, "Cascadia Code", "Segoe UI Mono", "Roboto Mono", monospace' }}
        />
      )}

      {showInline && grammarFixes.length > 0 && (
        <div className="mt-2 text-xs flex items-center gap-1.5" style={{color:'var(--ink-3)'}}>
          <Info size={11}/> Hover to see the proposed fix. Click to apply.
        </div>
      )}

      {showPolish && polishSuggestions.length > 0 && (
        <div className="mt-4 card p-4" data-testid="polish-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="eyebrow mb-1" style={{color:'var(--accent-2)'}}>Suggested fixes</div>
              <div className="text-sm" style={{color:'var(--ink-2)'}}>Grammar, punctuation, and clarity. Voice is preserved.</div>
            </div>
            <div className="flex gap-2">
              <button onClick={applyAllPolish} data-testid="polish-apply-all" className="btn btn-accent text-xs py-1.5 px-3">Apply all ({polishSuggestions.length})</button>
              <button onClick={()=>setShowPolish(false)} className="btn btn-ghost text-xs py-1.5 px-3">Dismiss</button>
            </div>
          </div>
          <div className="space-y-2">
            {polishSuggestions.map((s, i) => (
              <div key={i} className="p-3 rounded-2xl" style={{background:'var(--bg-soft)'}} data-testid={`polish-sug-${i}`}>
                <div className="text-xs mb-2" style={{color:'var(--ink-3)'}}>{s.reason || 'Suggested edit'}</div>
                <div className="flex items-center gap-2 mb-1 text-xs">
                  <span style={{background:'#fde5df', color:'#8f2b1a', textDecoration:'line-through', padding:'2px 6px', borderRadius:8}}>{s.before}</span>
                  <span>→</span>
                  <span style={{background:'var(--accent-tint)', color:'var(--accent-2)', padding:'2px 6px', borderRadius:8}}>{s.after}</span>
                </div>
                <button onClick={()=>applyPolishSuggestion(s)} data-testid={`polish-apply-${i}`} className="btn btn-ghost text-xs py-1 px-2 mt-1">Apply</button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  const ScorePane = (
    <div className="h-full overflow-y-auto scroll-thin pr-1">
      <GrowthScore
        text={postText}
        grammarFixes={grammarFixes}
        setGrammarFixes={setGrammarFixes}
        onApplyFix={applyGrammarFix}
      />
    </div>
  );

  // Detect if we should render the desktop resizable layout vs mobile stacked layout.
  // Uses a viewport listener so tests find each interactive element only once.
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 1024px)").matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(min-width: 1024px)");
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener?.("change", handler);
    return () => mq.removeEventListener?.("change", handler);
  }, []);

  // Default sizes are declared inline on each Panel via defaultSize props.

  return (
    <div className="flex flex-col" style={{ height: "calc(100vh - 40px)", maxHeight: "calc(100vh - 40px)" }}>
      <div className="flex items-center justify-between gap-3 py-3 shrink-0" data-testid="draft-toolbar-top">
        <div className="eyebrow" style={{color:'var(--ink-3)'}}>Drafting Studio</div>
        <div className="flex items-center gap-2 relative">
          <button data-testid="draft-history-btn" className="btn btn-ghost btn-sm" onClick={() => { if (!showSessions) loadSessions(); setShowSessions(s => !s); }} title="Previous chats">
            <ClockCounterClockwise size={14}/> History {sessions.length > 0 && <span className="ml-1 text-xs" style={{color:'var(--ink-3)'}}>· {sessions.length}</span>}
          </button>
          {showSessions && (
            <>
              <div className="fixed inset-0 z-40" onClick={()=>setShowSessions(false)} />
              <div
                data-testid="draft-history-panel"
                className="absolute right-0 top-12 z-50 w-[380px] max-h-[70vh] overflow-y-auto scroll-thin rounded-3xl p-2 fade-up"
                style={{background:'var(--bg-elev)', boxShadow:'var(--sh-lift)', border:'1px solid var(--line-2)'}}
                onClick={(e)=>e.stopPropagation()}
              >
                <div className="px-3 py-2 flex items-center justify-between">
                  <div className="eyebrow">Recent chats</div>
                  <span className="text-xs font-mono" style={{color:'var(--ink-3)'}}>{sessions.length} total</span>
                </div>
                {sessions.length === 0 ? (
                  <div className="p-6 text-sm text-center" style={{color:'var(--ink-3)'}}>No chats yet.<br/>Start writing.</div>
                ) : sessions.map((s) => {
                  const active = s.session_id === sessionId;
                  return (
                    <div
                      key={s.session_id}
                      data-testid={`draft-session-${s.session_id}`}
                      className={`group px-3 py-2.5 rounded-2xl cursor-pointer flex items-start gap-2 ${active ? '' : ''}`}
                      style={active ? {background:'var(--accent-tint)', border:'1px solid var(--accent)'} : {background:'transparent'}}
                      onClick={()=>switchSession(s.session_id)}
                      onMouseEnter={(e)=>{ if(!active) e.currentTarget.style.background='var(--bg-soft)';}}
                      onMouseLeave={(e)=>{ if(!active) e.currentTarget.style.background='transparent';}}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium truncate" style={active ? {color:'var(--accent-2)'} : {}}>{s.title}</div>
                        <div className="text-xs mt-0.5 truncate" style={{color:'var(--ink-3)'}}>{s.preview || 'No reply yet'}</div>
                        <div className="text-[11px] mt-1 font-mono" style={{color:'var(--ink-4)'}}>
                          {s.turns} turns · {s.last_at ? new Date(s.last_at).toLocaleDateString(undefined,{month:'short', day:'numeric'}) + ' ' + new Date(s.last_at).toLocaleTimeString(undefined,{hour:'2-digit',minute:'2-digit'}) : ''}
                        </div>
                      </div>
                      <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          data-testid={`draft-session-rename-${s.session_id}`}
                          onClick={(e)=>{e.stopPropagation(); renameSession(s.session_id, s.title);}}
                          className="p-1.5 rounded-full hover:bg-white"
                          title="Rename"
                        ><PencilSimple size={12}/></button>
                        <button
                          data-testid={`draft-session-delete-${s.session_id}`}
                          onClick={(e)=>{e.stopPropagation(); deleteSession(s.session_id, s.title);}}
                          className="p-1.5 rounded-full hover:bg-white"
                          style={{color:'#b83a2b'}}
                          title="Delete chat"
                        ><Trash size={12}/></button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
          <button data-testid="draft-save-btn" className="btn btn-ghost btn-sm" onClick={saveToPlanner} disabled={saving||!postText}><FloppyDisk size={14}/> Save draft</button>
          {(suggestedTag || classifying) && postText && (
            <span
              data-testid="draft-suggested-type"
              className="chip inline-flex items-center gap-1 text-[11px]"
              style={{
                padding: "3px 8px",
                ...(suggestedTag
                  ? { background: (
                      suggestedTag.toLowerCase() === "attract"  ? "#FEE3D8" :
                      suggestedTag.toLowerCase() === "nurture"  ? "#E4EFDD" :
                      suggestedTag.toLowerCase() === "position" ? "#E3E7F5" :
                      suggestedTag.toLowerCase() === "convert"  ? "#F7DFEB" : "var(--paper)"),
                     color: (
                      suggestedTag.toLowerCase() === "attract"  ? "#B14818" :
                      suggestedTag.toLowerCase() === "nurture"  ? "#3C6B23" :
                      suggestedTag.toLowerCase() === "position" ? "#334095" :
                      suggestedTag.toLowerCase() === "convert"  ? "#8A2E63" : "var(--ink-2)"),
                     borderColor: "transparent" }
                  : {}),
              }}
              title="Auto-detected content-marketing type — carried into the Planner on save"
            >
              {classifying && !suggestedTag ? "· classifying…" : <>Type: <strong>{suggestedTag}</strong></>}
            </span>
          )}
          <button data-testid="draft-new-btn" className="btn btn-accent btn-sm" onClick={newChat} title="Start a new draft"><Plus size={16} weight="bold"/></button>
        </div>
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-3 py-2 shrink-0" data-testid="draft-toolbar-controls">
        <div className="flex items-center gap-1.5">
          <span className="eyebrow">Mode</span>
          <div className="seg" role="tablist" aria-label="Reply mode" data-testid="draft-mode-toggle">
            <button
              onClick={() => setMode("auto")}
              data-active={mode === "auto"}
              data-testid="draft-mode-auto"
              title="Auto — she picks based on your message"
            >Auto</button>
            <button
              onClick={() => setMode("chat")}
              data-active={mode === "chat"}
              data-testid="draft-mode-chat"
              title="Chat only — she answers questions, never rewrites your post"
            >Chat</button>
            <button
              onClick={() => setMode("post")}
              data-active={mode === "post"}
              data-testid="draft-mode-post"
              title="Post — she always returns publish-ready post copy"
            >Post</button>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="eyebrow">Type</span>
          <select data-testid="draft-post-type" className="select w-auto" value={postType} onChange={e=>setPostType(e.target.value)} title={POST_TYPE_META[postType]?.hint}>
            {Object.entries(POST_TYPE_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="eyebrow">Tone</span>
          <select data-testid="draft-tone" className="select w-auto" value={tone} onChange={e=>setTone(e.target.value)} title={TONE_HINT[tone]}>
            <option value="personal">personal</option>
            <option value="balanced">balanced</option>
            <option value="corporate">corporate</option>
            <option value="contrarian">contrarian</option>
          </select>
        </div>
        <span
          className="hidden md:inline-flex items-center gap-1 text-xs max-w-md"
          style={{ color: 'var(--ink-3)' }}
          title={POST_TYPE_META[postType]?.hint}
          data-testid="draft-post-type-hint"
        >
          <Info size={12}/> {POST_TYPE_META[postType]?.hint}
        </span>
        <label className="flex items-center gap-2 cursor-pointer chip">
          <input data-testid="draft-swipes-toggle" type="checkbox" checked={useSwipes} onChange={e=>setUseSwipes(e.target.checked)} />
          <Scissors size={12}/><span className="text-xs">Use swipes</span>
        </label>
        <div className="flex-1"/>
        <button
          data-testid="draft-toggle-preview"
          onClick={()=>setShowPreview(v=>!v)}
          className="btn btn-ghost btn-sm"
          disabled={!postText}
        >
          <Eye size={13}/> LinkedIn preview
        </button>
      </div>

      {/* Desktop: resizable 3-panel layout. Mobile: stacked. */}
      {isDesktop ? (
        <div className="flex-1 min-h-0">
          <PanelGroup
            orientation="horizontal"
            defaultLayout={defaultLayout}
            onLayoutChanged={onLayoutChanged}
            className="h-full flex"
            data-testid="draft-panels"
          >
            <Panel defaultSize="38%" minSize="22%" data-testid="draft-panel-chat">
              {ChatPane}
            </Panel>
            <PanelResizeHandle className="bl-resize-handle" data-testid="draft-resize-1" />
            <Panel defaultSize="40%" minSize="26%" data-testid="draft-panel-editor">
              <div className="h-full px-3">{DraftPane}</div>
            </Panel>
            <PanelResizeHandle className="bl-resize-handle" data-testid="draft-resize-2" />
            <Panel defaultSize="22%" minSize="18%" data-testid="draft-panel-score">
              <div className="h-full pl-3">{ScorePane}</div>
            </Panel>
          </PanelGroup>
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex flex-col gap-4 overflow-y-auto scroll-thin">
          <div style={{ minHeight: 320 }}>{ChatPane}</div>
          <div>{DraftPane}</div>
          <div>{ScorePane}</div>
        </div>
      )}

      {showPreview && (
        <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto" style={{background:'rgba(0,0,0,0.55)'}} onClick={()=>setShowPreview(false)}>
          <div onClick={e=>e.stopPropagation()} className="card w-full max-w-2xl fade-up my-8 max-h-[92vh] flex flex-col" data-testid="preview-modal">
            <div className="flex items-center justify-between gap-3 p-5 pb-3 shrink-0 flex-wrap" style={{borderBottom:'0'}}>
              <div className="flex items-center gap-3 flex-wrap">
                <div className="eyebrow">LinkedIn preview</div>
                <div className="seg" role="tablist" aria-label="Preview device">
                  <button data-testid="draft-view-mobile" onClick={()=>setDevice("mobile")} data-active={device==='mobile'}><DeviceMobile size={13}/> Mobile</button>
                  <button data-testid="draft-view-desktop" onClick={()=>setDevice("desktop")} data-active={device==='desktop'}><Desktop size={13}/> Desktop</button>
                </div>
              </div>
              <button onClick={()=>setShowPreview(false)} className="p-2 rounded-full hover:bg-[color:var(--paper)]" title="Close" aria-label="Close preview"><EyeSlash size={16}/></button>
            </div>
            <div className="px-5 pb-5 overflow-y-auto scroll-thin flex-1 min-h-0">
              <div className="mx-auto" style={{ maxWidth: device==='mobile' ? 420 : 600 }}>
                <div className="li-preview" data-testid="draft-preview">
                  <div className="hd">
                    <div className="avatar">{initial}</div>
                    <div>
                      <div style={{fontWeight:600, fontSize:14}}>{authorName}</div>
                      <div style={{fontSize:12, color:'#666'}}>{user?.email} · Now · 🌐</div>
                    </div>
                  </div>
                  <div style={{whiteSpace:'pre-wrap'}}>{previewText}</div>
                  <div style={{marginTop: 8, fontSize: 12, color:'#666'}}>
                    {previewText.length} chars · fold at ~210 chars on mobile
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
